/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Modal Error Boundary - Catches errors in modal components without breaking the page
 *
 * Unlike the full-page ErrorBoundary, this component silently catches errors
 * and renders nothing, allowing the rest of the application to continue functioning.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { Component } from 'react'
import PropTypes from 'prop-types'
import loggingService from '../services/loggingService'
import { createLogger } from '../utils/logger'

const logger = createLogger('ModalErrorBoundary')

class ModalErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    const { name = 'Unknown Modal' } = this.props

    // Log the error
    loggingService.error(`Modal Error: ${name}`, {
      type: 'modal_error',
      modalName: name,
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    })

    logger.error(`[ModalErrorBoundary] Error in ${name}:`, error)

    // Store error for debugging
    try {
      const errorData = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        message: `Modal Error (${name}): ${error.toString()}`,
        stack: error.stack,
        url: window.location.href,
        type: 'modal-error',
        modalName: name
      }

      const storedErrors = JSON.parse(localStorage.getItem('errorHistory') || '[]')
      const updatedErrors = [errorData, ...storedErrors].slice(0, 100)
      localStorage.setItem('errorHistory', JSON.stringify(updatedErrors))
    } catch (storageError) {
      // Ignore storage errors
    }
  }

  render() {
    if (this.state.hasError) {
      // Return null to hide the modal instead of showing an error UI
      // This prevents modal errors from breaking the entire application
      return null
    }

    return this.props.children
  }
}

ModalErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string
}

ModalErrorBoundary.defaultProps = {
  name: 'Unknown Modal'
}

export default ModalErrorBoundary
